/**
 * Custom node components for the visual graph builder.
 *
 * Each node type renders differently on the canvas to visually
 * distinguish agent, router, orchestrator, function, and terminal nodes.
 */
import React, { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { Bot, GitBranch, Cpu, Zap, Play, Square } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

// ---------------------------------------------------------------------------
// Shared node wrapper
// ---------------------------------------------------------------------------

function NodeShell({
  children,
  selected,
  className = '',
}: {
  children: React.ReactNode;
  selected?: boolean;
  className?: string;
}) {
  return (
    <div
      className={`rounded-lg border bg-background shadow-sm px-3 py-2 min-w-[160px] max-w-[220px] transition-all ${
        selected ? 'ring-2 ring-primary border-primary' : 'border-border'
      } ${className}`}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Node types
// ---------------------------------------------------------------------------

export const AgentNode = memo(({ data, selected }: NodeProps) => (
  <NodeShell selected={selected}>
    <Handle type="target" position={Position.Top} className="!bg-primary !w-2.5 !h-2.5" />
    <div className="flex items-center gap-2 mb-1">
      <Bot className="h-3.5 w-3.5 text-blue-500 shrink-0" />
      <span className="text-sm font-medium truncate">{data.label as string}</span>
    </div>
    {data.model ? (
      <Badge variant="outline" className="text-[10px] mb-1">{String(data.model)}</Badge>
    ) : null}
    {data.prompt ? (
      <p className="text-[10px] text-muted-foreground line-clamp-2">
        {String(data.prompt).slice(0, 80)}
      </p>
    ) : null}
    {data.toolCount != null && (data.toolCount as number) > 0 && (
      <Badge variant="secondary" className="text-[10px] mt-1">
        {data.toolCount as number} tool{(data.toolCount as number) !== 1 ? 's' : ''}
      </Badge>
    )}
    <Handle type="source" position={Position.Bottom} className="!bg-primary !w-2.5 !h-2.5" />
  </NodeShell>
));
AgentNode.displayName = 'AgentNode';

export const RouterNode = memo(({ data, selected }: NodeProps) => (
  <NodeShell selected={selected} className="bg-amber-50 dark:bg-amber-950/20">
    <Handle type="target" position={Position.Top} className="!bg-amber-500 !w-2.5 !h-2.5" />
    <div className="flex items-center gap-2">
      <GitBranch className="h-3.5 w-3.5 text-amber-500 shrink-0" />
      <span className="text-sm font-medium truncate">{data.label as string}</span>
    </div>
    <p className="text-[10px] text-muted-foreground">Conditional routing</p>
    <Handle type="source" position={Position.Bottom} className="!bg-amber-500 !w-2.5 !h-2.5" />
  </NodeShell>
));
RouterNode.displayName = 'RouterNode';

export const OrchestratorNode = memo(({ data, selected }: NodeProps) => (
  <NodeShell selected={selected} className="bg-purple-50 dark:bg-purple-950/20 min-w-[200px]">
    <Handle type="target" position={Position.Top} className="!bg-purple-500 !w-2.5 !h-2.5" />
    <div className="flex items-center gap-2 mb-1">
      <Cpu className="h-3.5 w-3.5 text-purple-500 shrink-0" />
      <span className="text-sm font-medium truncate">{data.label as string}</span>
    </div>
    {Array.isArray(data.workers) && (data.workers as string[]).length > 0 ? (
      <div className="flex flex-wrap gap-1">
        {(data.workers as string[]).map((w: string) => (
          <Badge key={w} variant="outline" className="text-[10px]">{w}</Badge>
        ))}
      </div>
    ) : null}
    <Handle type="source" position={Position.Bottom} className="!bg-purple-500 !w-2.5 !h-2.5" />
  </NodeShell>
));
OrchestratorNode.displayName = 'OrchestratorNode';

export const FunctionNode = memo(({ data, selected }: NodeProps) => (
  <NodeShell selected={selected} className="bg-green-50 dark:bg-green-950/20">
    <Handle type="target" position={Position.Top} className="!bg-green-500 !w-2.5 !h-2.5" />
    <div className="flex items-center gap-2">
      <Zap className="h-3.5 w-3.5 text-green-500 shrink-0" />
      <span className="text-sm font-medium truncate">{data.label as string}</span>
    </div>
    {data.functionName ? (
      <p className="text-[10px] text-muted-foreground font-mono">{String(data.functionName)}</p>
    ) : null}
    <Handle type="source" position={Position.Bottom} className="!bg-green-500 !w-2.5 !h-2.5" />
  </NodeShell>
));
FunctionNode.displayName = 'FunctionNode';

export const StartNode = memo(({ selected }: NodeProps) => (
  <div
    className={`rounded-full border-2 bg-emerald-500 text-white w-12 h-12 flex items-center justify-center shadow-sm ${
      selected ? 'ring-2 ring-primary ring-offset-2' : ''
    }`}
  >
    <Play className="h-5 w-5 ml-0.5" />
    <Handle type="source" position={Position.Bottom} className="!bg-emerald-700 !w-2.5 !h-2.5" />
  </div>
));
StartNode.displayName = 'StartNode';

export const EndNode = memo(({ selected }: NodeProps) => (
  <div
    className={`rounded-full border-2 bg-red-500 text-white w-12 h-12 flex items-center justify-center shadow-sm ${
      selected ? 'ring-2 ring-primary ring-offset-2' : ''
    }`}
  >
    <Square className="h-4 w-4" />
    <Handle type="target" position={Position.Top} className="!bg-red-700 !w-2.5 !h-2.5" />
  </div>
));
EndNode.displayName = 'EndNode';

// ---------------------------------------------------------------------------
// Node type registry
// ---------------------------------------------------------------------------

export const nodeTypes = {
  agent: AgentNode,
  router: RouterNode,
  orchestrator: OrchestratorNode,
  function: FunctionNode,
  start: StartNode,
  end: EndNode,
};
